import { useState, useEffect } from "react";
import { useAuth, database } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ref, get } from "firebase/database";
import { BarChart3, Users, Code, TrendingUp } from "lucide-react";

export default function DashboardPage() {
  const { authToken } = useAuth();
  const [stats, setStats] = useState({
    total_projects: 0,
    total_users: 0,
    total_tech_stacks: 0,
    top_tech_stack: [],
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch projects
      const projectSnap = await get(ref(database, "projects"));
      const projectData = projectSnap.exists() ? projectSnap.val() : {};

      const projectList = Object.values(projectData);

      // Total projects
      const totalProjects = projectList.length;

      // Collect all tech stacks
      const allTechs = projectList.flatMap((p) => p.tech_stack || []);

      // Unique tech stack count
      const uniqueTechStack = [...new Set(allTechs)];

      // Count frequency for MOST POPULAR tech stacks
      const techFrequency = {};
      allTechs.forEach((tech) => {
        techFrequency[tech] = (techFrequency[tech] || 0) + 1;
      });

      const sortedTopTech = Object.entries(techFrequency)
        .map(([name, count]) => ({
          _id: name,
          count,
        }))
        .sort((a, b) => b.count - a.count); // descending

      // Fetch users
      const userSnap = await get(ref(database, "users"));
      const userData = userSnap.exists() ? userSnap.val() : {};
      const totalUsers = Object.keys(userData).length;

      setStats({
        total_projects: totalProjects,
        total_users: totalUsers,
        total_tech_stacks: uniqueTechStack.length,
        top_tech_stack: sortedTopTech,
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to load stats");
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass p-6 rounded-2xl skeleton h-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Title */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">Dashboard</h1>
          <p className="text-gray-600">Platform insights and statistics</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          
          {/* Total Projects */}
          <Card className="glass border-white/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <Code className="w-10 h-10 text-sky-500" />
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div className="text-4xl font-bold gradient-text mb-2">
                {stats.total_projects}
              </div>
              <div className="text-gray-600">Total Projects</div>
            </CardContent>
          </Card>

          {/* Total Users */}
          <Card className="glass border-white/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-10 h-10 text-cyan-500" />
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div className="text-4xl font-bold gradient-text mb-2">
                {stats.total_users}
              </div>
              <div className="text-gray-600">Active Students</div>
            </CardContent>
          </Card>

          {/* Total Tech Stacks */}
          <Card className="glass border-white/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <BarChart3 className="w-10 h-10 text-blue-500" />
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div className="text-4xl font-bold gradient-text mb-2">
                {stats.total_tech_stacks}
              </div>
              <div className="text-gray-600">Tech Stacks</div>
            </CardContent>
          </Card>
        </div>

        {/* Top Technologies */}
        <Card className="glass border-white/50">
          <CardHeader>
            <CardTitle>Most Popular Tech Stack</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_tech_stack.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No data available yet
              </p>
            ) : (
              <div className="space-y-4">
                {stats.top_tech_stack.map((tech, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-white/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-cyan-500 flex items-center justify-center text-white font-bold">
                        {index + 1}
                      </div>
                      <Badge variant="secondary" className="text-base">
                        {tech._id}
                      </Badge>
                    </div>
                    <div className="text-2xl font-bold text-sky-600">
                      {tech.count}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
